/**
 * GDPR Consent Manager
 * Centralized service for managing user consent and conditional service loading
 */

import { useAuthStore } from '../store/authStore';

interface ConsentPreferences {
  necessary: boolean;        // Always true
  analytics: boolean;        // Sentry enhanced tracking
  marketing: boolean;        // Omnisend, conversion tracking  
  personalization: boolean;  // A/B testing, journey tracking
}

interface ConsentRecord {
  preferences: ConsentPreferences;
  timestamp: string;
  version: string;
  method: 'banner' | 'settings' | 'implied';
}

class ConsentManager {
  private static instance: ConsentManager;
  private consent: ConsentRecord | null = null;
  private listeners: Array<(consent: ConsentPreferences) => void> = [];
  
  private readonly STORAGE_KEY = 'botblender_consent';
  private readonly CONSENT_VERSION = '1.0';
  
  static getInstance(): ConsentManager {
    if (!ConsentManager.instance) {
      ConsentManager.instance = new ConsentManager();
    }
    return ConsentManager.instance;
  }
  
  private constructor() {
    this.loadStoredConsent();
  }
  
  /**
   * Load consent from localStorage and validate
   */
  private loadStoredConsent(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate consent is still current version
        if (parsed.version === this.CONSENT_VERSION) {
          this.consent = parsed;
        } else {
          console.log('[ConsentManager] Consent version outdated, requiring new consent');
          this.clearStoredConsent();
        }
      }
    } catch (error) {
      console.warn('[ConsentManager] Error loading stored consent:', error);
      this.clearStoredConsent();
    }
  }
  
  /**
   * Check if user has provided consent
   */
  hasConsent(): boolean {
    return this.consent !== null;
  }
  
  /**
   * Get current consent preferences
   */
  getConsent(): ConsentPreferences | null {
    return this.consent?.preferences || null;
  }
  
  /**
   * Check consent for specific category
   */
  hasConsentFor(category: keyof ConsentPreferences): boolean {
    const preferences = this.getConsent();
    return preferences?.[category] || false;
  }
  
  /**
   * Update consent preferences
   */
  async updateConsent(
    preferences: Omit<ConsentPreferences, 'necessary'>,
    method: 'banner' | 'settings' = 'banner'
  ): Promise<void> {
    const fullPreferences: ConsentPreferences = {
      necessary: true, // Always true
      ...preferences
    };
    
    const consentRecord: ConsentRecord = {
      preferences: fullPreferences,
      timestamp: new Date().toISOString(),
      version: this.CONSENT_VERSION,
      method
    };
    
    // Store locally first for immediate effect
    this.consent = consentRecord;
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(consentRecord));
    
    // Notify listeners of consent change
    this.notifyListeners(fullPreferences);
    
    // Sync to backend (non-blocking)
    try {
      await this.syncToBackend(preferences, method);
    } catch (error) {
      console.error('[ConsentManager] Failed to sync consent to backend:', error);
      // Don't fail the consent update if backend sync fails
    }
  }
  
  /**
   * Sync consent to backend
   */
  private async syncToBackend(
    preferences: Omit<ConsentPreferences, 'necessary'>,
    method: string
  ): Promise<void> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      console.warn('[ConsentManager] No access token available for backend sync');
      return;
    }

    const response = await fetch('/api/gdpr/consent-preferences', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        analytics_cookies: preferences.analytics,
        marketing_cookies: preferences.marketing,
        personalization_cookies: preferences.personalization,
        consent_method: method,
        data_retention_acknowledged: true
      })
    });
    
    if (!response.ok) {
      throw new Error(`Backend sync failed: ${response.statusText}`);
    }
  }
  
  /**
   * Get access token for API calls
   */
  private async getAccessToken(): Promise<string | null> {
    const authStore = useAuthStore.getState();
    return authStore.accessToken;
  }
  
  /**
   * Add consent change listener
   */
  onConsentChange(callback: (consent: ConsentPreferences) => void): () => void {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }
  
  /**
   * Notify all listeners of consent changes
   */
  private notifyListeners(preferences: ConsentPreferences): void {
    this.listeners.forEach(callback => {
      try {
        callback(preferences);
      } catch (error) {
        console.error('[ConsentManager] Error in consent listener:', error);
      }
    });
  }
  
  /**
   * Clear stored consent (for testing or version updates)
   */
  private clearStoredConsent(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.consent = null;
  }
  
  /**
   * Initialize services based on consent
   */
  initializeServices(): void {
    const preferences = this.getConsent();
    if (!preferences) {
      console.log('[ConsentManager] No consent available, skipping service initialization');
      return;
    }
    
    // Initialize analytics services
    if (preferences.analytics) {
      this.initializeSentry();
    }
    
    // Initialize marketing services  
    if (preferences.marketing) {
      this.initializeOmnisend();
    }
    
    // Initialize personalization services
    if (preferences.personalization) {
      this.initializeABTesting();
      this.initializeJourneyTracking();
    }
    
    console.log('[ConsentManager] Services initialized based on consent:', preferences);
  }
  
  /**
   * Initialize Sentry with consent-appropriate configuration
   */
  private initializeSentry(): void {
    // Enhanced Sentry with session replay
    if (window.Sentry) {
      window.Sentry.setTag('consent_analytics', true);
      console.log('[ConsentManager] Sentry enhanced analytics enabled');
    }
  }
  
  /**
   * Initialize Omnisend marketing tracking
   */
  private initializeOmnisend(): void {
    if (!window.omnisend) {
      // Load Omnisend script dynamically
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://omnisnippet1.com/inshop/launcher-v2.js';
      
      script.onload = () => {
        window.omnisend = window.omnisend || [];
        window.omnisend.push(['brandID', '68b8292917ed960e3182ff98']);
        window.omnisend.push(['track', '$pageViewed']);
        console.log('[ConsentManager] Omnisend marketing tracking enabled');
      };
      
      document.head.appendChild(script);
    }
  }
  
  /**
   * Initialize A/B testing
   */
  private initializeABTesting(): void {
    // Enable A/B testing data collection
    localStorage.setItem('ab_testing_enabled', 'true');
    console.log('[ConsentManager] A/B testing enabled');
  }
  
  /**
   * Initialize journey tracking
   */
  private initializeJourneyTracking(): void {
    // Enable journey/conversion tracking
    import('../utils/journeyTracking').then(({ JourneyTracker }) => {
      const source = sessionStorage.getItem('original_source') || 'direct';
      JourneyTracker.startJourney(source);
      console.log('[ConsentManager] Journey tracking enabled');
    }).catch(error => {
      console.warn('[ConsentManager] Journey tracking module not available:', error);
    });
  }

  /**
   * Load consent preferences from backend for authenticated users
   */
  async loadConsentFromBackend(): Promise<void> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      console.log('[ConsentManager] No access token available, skipping backend consent load');
      return;
    }

    try {
      const response = await fetch('/api/gdpr/consent-preferences', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const backendConsent = await response.json();
        
        if (backendConsent.needs_consent) {
          // User needs to provide consent, clear local storage
          this.clearStoredConsent();
          console.log('[ConsentManager] User needs to provide consent');
        } else {
          // User has existing consent preferences
          const consentRecord: ConsentRecord = {
            preferences: {
              necessary: backendConsent.necessary_cookies || true,
              analytics: backendConsent.analytics_cookies || false,
              marketing: backendConsent.marketing_cookies || false,
              personalization: backendConsent.personalization_cookies || false
            },
            timestamp: backendConsent.consent_timestamp || new Date().toISOString(),
            version: backendConsent.consent_version || this.CONSENT_VERSION,
            method: 'implied' // Backend loaded consent
          };

          this.consent = consentRecord;
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(consentRecord));
          console.log('[ConsentManager] Loaded consent preferences from backend:', consentRecord.preferences);
        }
      }
    } catch (error) {
      console.error('[ConsentManager] Failed to load consent from backend:', error);
    }
  }
}

export const consentManager = ConsentManager.getInstance();
export type { ConsentPreferences, ConsentRecord };

// Add global types for window extensions
declare global {
  interface Window {
    omnisend: any[];
    Sentry: any;
  }
}